import { Box, Flex, Icon, IconButton, Table, Tbody, Td, Text, Th, Thead, Tr } from "@chakra-ui/react";
import { t } from "i18next";
import { Dispatch, SetStateAction, useState } from "react";
import { FaCompressAlt, FaExpandAlt } from "react-icons/fa";
import { BseDto, ReceivedBseBidDto } from "../../../../../autogen/bff-api";
import { Message } from "../../../../../common/ConversationModal";
import { formatNumber } from "../../../../../common/formatting/formatNumber";
import { Product, ProductDraft, ProductGroup } from "../../../../../common/types";
import { eventIsCompleted } from "../../../eventIsCompleted";
import { useNegotiation } from "../NegotiationContext";
import {
  findProductGroupBid,
  getProductGroupPrice,
  getSelectedOrWinningBids,
  getSelectedProductGroupPrice,
  getSumOfBestGroupPrices,
} from "./BidEvaluationUtils";
import { EvaluationReportRow } from "./EvaluationReportRow";
import { EvaluationRow } from "./EvaluationRow";
import { useBidSelection } from "./useSelectProductBids";

export const EvaluationTable = ({
  group,
  sourcingEvent,
  bids,
  setProductConversation,
}: {
  group: ProductGroup;
  sourcingEvent: BseDto;
  bids?: ReceivedBseBidDto[];
  setProductConversation: Dispatch<
    SetStateAction<
      | {
          conversation: Message[];
          title: string;
          productGroup: ProductGroup;
          product: Product | ProductDraft;
        }
      | undefined
    >
  >;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>();

  const { currentRound } = useNegotiation();
  const { selectAllBidsInGroupFromSupplier, selectionLocked } = useBidSelection();

  const selectedBids = getSelectedOrWinningBids(sourcingEvent, currentRound);

  return (
    <Box
      key={group.id}
      position={isExpanded ? "absolute" : "relative"}
      zIndex={isExpanded ? "docked" : "auto"}
      left="0"
      width={"full"}
      maxWidth={"full"}
      backgroundColor="smBackground"
      border="1px solid"
      borderColor={"teal.100"}
      rounded={"lg"}
      mb="8"
      p="5"
      overflow="hidden"
    >
      <Flex alignItems={"center"} pb="4" pl="4" justifyContent={"space-between"}>
        <Text size="lg" fontWeight={"bold"}>
          {group.name}
        </Text>
        <IconButton
          aria-label="Toggle product group size"
          icon={<Icon as={isExpanded ? FaCompressAlt : FaExpandAlt} />}
          size="sm"
          variant={"ghost"}
          onClick={() => setIsExpanded(!isExpanded)}
        />
      </Flex>
      <Box width={"full"} maxWidth={"full"} overflow={"auto"} rounded={"lg"}>
        <Table variant={"simple"} size="sm">
          <Thead>
            <Tr>
              {group.productFields
                .filter((field) => field.populatedBy === "Buyer")
                .map((field) => (
                  <Th key={field.name} minWidth="40" p="1">
                    {field.name}
                  </Th>
                ))}
              <Th minWidth="40" p="1">
                {t("Quantity")}
              </Th>
              {!eventIsCompleted({ event: sourcingEvent, round: currentRound }) &&
                bids?.map((bid) => (
                  <Th
                    key={bid.id}
                    p="1"
                    textAlign={"right"}
                    cursor={selectionLocked ? "default" : "pointer"}
                    onClick={selectionLocked ? undefined : () => selectAllBidsInGroupFromSupplier({ group, bid })}
                  >
                    {bid.owningOrganization.name}
                  </Th>
                ))}
              {!eventIsCompleted({ event: sourcingEvent, round: currentRound }) && (
                <>
                  <Th minWidth="40" p="1" textAlign={"right"}>
                    {t("Best Price")}
                  </Th>
                  <Th minWidth="40" p="1" textAlign={"right"}>
                    {t("Selected Price")}
                  </Th>
                </>
              )}
              {eventIsCompleted({ event: sourcingEvent, round: currentRound }) && (
                <>
                  <Th p="1" textAlign={"right"}>
                    {t("Total Price")}
                  </Th>
                  <Th p="1" textAlign={"center"}>
                    {t("Selected Supplier")}
                  </Th>
                </>
              )}
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {group.products?.map((product) =>
              eventIsCompleted({ event: sourcingEvent, round: currentRound }) ? (
                <EvaluationReportRow
                  key={product.id}
                  sourcingEvent={sourcingEvent}
                  bids={bids}
                  group={group}
                  product={product as Product}
                  setProductConversation={setProductConversation}
                />
              ) : (
                <EvaluationRow
                  key={product.id}
                  bids={bids}
                  group={group}
                  product={product as Product}
                  setProductConversation={setProductConversation}
                  selectedBids={selectedBids}
                />
              )
            )}
            <Tr fontWeight={"bold"}>
              <Td p="1" colSpan={group.productFields.filter((field) => field.populatedBy === "Buyer").length + 1}>
                SUM
              </Td>
              {!eventIsCompleted({ event: sourcingEvent, round: currentRound }) &&
                bids?.map((bid) => (
                  <Td key={bid.id} p="1" textAlign={"right"}>
                    {formatNumber(
                      getProductGroupPrice({
                        groupBid: findProductGroupBid({ groupId: group.id, bid }),
                        productGroup: group,
                      }),
                    )}
                  </Td>
                ))}
              {!eventIsCompleted({ event: sourcingEvent, round: currentRound }) && (
                <Td p="1" textAlign={"right"}>
                  {formatNumber(getSumOfBestGroupPrices({ group, bids: bids ?? [] }))}
                </Td>
              )}
              <Td p="1" textAlign={"right"}>
                {formatNumber(getSelectedProductGroupPrice({ group, bids: bids ?? [], selectedBids }))}
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
